import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  Mail,
  Phone,
  User,
  Clock,
  Trash2,
  Inbox,
  Loader2,
  Lock,
  ArrowLeft,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "ccc_admin_token";

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const navigate = useNavigate();

  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const loadInquiries = async (tk) => {
    const useToken = tk ?? token;
    if (!useToken) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/inquiries`, {
        headers: { Authorization: `Bearer ${useToken}` },
      });
      setInquiries(res.data);
      if (res.data[0]) setActiveId(res.data[0].id);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        toast.error("Your session expired. Please sign in again.");
      } else {
        toast.error("Could not load inquiries.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadInquiries(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogin = (newToken) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    setToken(newToken);
    loadInquiries(newToken);
  };

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setInquiries([]);
    toast.success("Signed out.");
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this inquiry? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/inquiries/${id}`, { headers: authHeader });
      setInquiries((list) => list.filter((i) => i.id !== id));
      toast.success("Inquiry deleted.");
    } catch (err) {
      toast.error("Could not delete inquiry.");
    }
  };

  if (!token) return <LoginCard onLogin={onLogin} onBack={() => navigate("/")} />;

  const active = inquiries.find((i) => i.id === activeId) || inquiries[0];

  return (
    <div className="min-h-screen bg-[#F9F8F6] text-[#1C2A24]" data-testid="admin-page">
      {/* Header */}
      <header className="border-b border-[#E5E1D8] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#2C4C3B] text-[#F4F1EA] font-heading text-lg">
              C
            </span>
            <div className="leading-tight">
              <p className="font-heading text-lg">Carlene&rsquo;s &middot; Admin</p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#667B68]">
                Inquiries
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/")}
              data-testid="admin-home-btn"
              className="hidden sm:inline-flex items-center gap-2 text-sm text-[#3E574B] hover:text-[#2C4C3B] px-3 py-2 rounded-full"
            >
              <ArrowLeft className="w-4 h-4" /> Website
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 text-sm bg-[#F4F1EA] hover:bg-[#E5E1D8] text-[#2C4C3B] px-4 py-2 rounded-full font-medium"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10">
        <div className="mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl">Inbox</h1>
          <p className="text-sm text-[#667B68] mt-1">
            {loading
              ? "Loading…"
              : `${inquiries.length} inquir${inquiries.length === 1 ? "y" : "ies"}, most recent first.`}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center gap-3 text-[#667B68]">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading inquiries…
          </div>
        ) : inquiries.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid lg:grid-cols-12 gap-4 lg:gap-6">
            {/* List */}
            <ul
              data-testid="admin-inquiry-list"
              className="lg:col-span-5 bg-white border border-[#E5E1D8] rounded-2xl divide-y divide-[#E5E1D8] overflow-hidden max-h-[70vh] overflow-y-auto"
            >
              {inquiries.map((inq) => (
                <li
                  key={inq.id}
                  data-testid={`admin-inquiry-item-${inq.id}`}
                >
                  <button
                    onClick={() => setActiveId(inq.id)}
                    className={`w-full text-left px-5 py-4 transition-colors ${
                      active?.id === inq.id
                        ? "bg-[#F4F1EA]"
                        : "hover:bg-[#F9F8F6]"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <p className="font-semibold text-[15px] truncate">
                        {inq.name}
                      </p>
                      <p className="text-[11px] text-[#667B68] shrink-0">
                        {formatDate(inq.created_at)}
                      </p>
                    </div>
                    <p className="text-sm text-[#3E574B] mt-1 truncate">
                      {inq.email}
                    </p>
                    <p className="text-sm text-[#1C2A24]/80 mt-2 line-clamp-2">
                      {inq.message}
                    </p>
                  </button>
                </li>
              ))}
            </ul>

            {/* Detail */}
            {active && (
              <section
                data-testid="admin-inquiry-detail"
                className="lg:col-span-7 bg-white border border-[#E5E1D8] rounded-2xl p-6 sm:p-8"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.22em] text-[#667B68] font-semibold">
                      From
                    </p>
                    <h2 className="font-heading text-2xl sm:text-3xl mt-1">
                      {active.name}
                    </h2>
                  </div>
                  <button
                    onClick={() => onDelete(active.id)}
                    data-testid="admin-delete-btn"
                    className="inline-flex items-center gap-2 text-sm text-[#C27A62] hover:text-white hover:bg-[#C27A62] border border-[#C27A62]/40 px-3 py-2 rounded-full transition"
                  >
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </div>

                <dl className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
                  <DetailRow icon={Mail} label="Email">
                    <a
                      href={`mailto:${active.email}`}
                      className="text-[#2C4C3B] hover:underline break-all"
                    >
                      {active.email}
                    </a>
                  </DetailRow>
                  <DetailRow icon={Phone} label="Phone">
                    {active.phone ? (
                      <a
                        href={`tel:${active.phone}`}
                        className="text-[#2C4C3B] hover:underline"
                      >
                        {active.phone}
                      </a>
                    ) : (
                      <span className="text-[#667B68]">—</span>
                    )}
                  </DetailRow>
                  <DetailRow icon={User} label="Relationship">
                    {active.relationship || (
                      <span className="text-[#667B68]">—</span>
                    )}
                  </DetailRow>
                  <DetailRow icon={Clock} label="Received">
                    {formatDateTime(active.created_at)}
                  </DetailRow>
                </dl>

                <div className="mt-8">
                  <p className="text-[11px] uppercase tracking-[0.22em] text-[#667B68] font-semibold mb-3">
                    Message
                  </p>
                  <div className="bg-[#F9F8F6] border-l-4 border-[#A67C52] rounded-xl p-5 text-[15px] leading-relaxed whitespace-pre-wrap">
                    {active.message}
                  </div>
                </div>

                <div className="mt-8 flex flex-wrap gap-2">
                  <a
                    href={`mailto:${active.email}?subject=Re: Your inquiry — Carlene's Comfort Care`}
                    className="inline-flex items-center gap-2 bg-[#2C4C3B] text-white hover:bg-[#1f372a] rounded-full px-5 py-2.5 text-sm font-medium"
                  >
                    <Mail className="w-4 h-4" /> Reply via email
                  </a>
                  {active.phone && (
                    <a
                      href={`tel:${active.phone}`}
                      className="inline-flex items-center gap-2 bg-[#F4F1EA] text-[#2C4C3B] hover:bg-[#E5E1D8] rounded-full px-5 py-2.5 text-sm font-medium"
                    >
                      <Phone className="w-4 h-4" /> Call
                    </a>
                  )}
                </div>
              </section>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function DetailRow({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3 bg-[#F9F8F6] rounded-xl px-4 py-3">
      <Icon className="w-4 h-4 text-[#667B68] mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#667B68] font-semibold">
          {label}
        </p>
        <div className="text-[14px] mt-0.5 break-words">{children}</div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div
      data-testid="admin-empty-state"
      className="bg-white border border-dashed border-[#E5E1D8] rounded-2xl p-12 text-center"
    >
      <Inbox className="w-8 h-8 text-[#667B68] mx-auto" />
      <p className="font-heading text-2xl mt-4">No inquiries yet</p>
      <p className="text-sm text-[#667B68] mt-2">
        When a family submits the contact form, it will show up here.
      </p>
    </div>
  );
}

function LoginCard({ onLogin, onBack }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/login`, {
        email: email.trim().toLowerCase(),
        password,
      });
      toast.success("Welcome back, Karie.");
      onLogin(res.data.access_token);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Invalid email or password.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <button
          onClick={onBack}
          data-testid="admin-back-btn"
          className="inline-flex items-center gap-2 text-sm text-[#667B68] hover:text-[#2C4C3B] mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back to website
        </button>

        <div className="bg-white border border-[#E5E1D8] rounded-3xl p-8 sm:p-10 shadow-[0_16px_48px_-24px_rgba(44,76,59,0.2)]">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#2C4C3B] text-[#F4F1EA]">
              <Lock className="w-5 h-5" />
            </span>
            <div>
              <p className="text-[11px] uppercase tracking-[0.25em] font-semibold text-[#667B68]">
                Private
              </p>
              <h1 className="font-heading text-2xl">Admin sign in</h1>
            </div>
          </div>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
            <label className="block">
              <span className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#3E574B] mb-2">
                Email
              </span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="admin-login-email"
                className="w-full bg-white border border-[#E5E1D8] focus:border-[#667B68] focus:ring-2 focus:ring-[#667B68]/30 focus:outline-none rounded-xl px-4 py-3 text-[15px] transition"
                placeholder="karie_richard@yahoo.com"
              />
            </label>
            <label className="block">
              <span className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#3E574B] mb-2">
                Password
              </span>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="admin-login-password"
                className="w-full bg-white border border-[#E5E1D8] focus:border-[#667B68] focus:ring-2 focus:ring-[#667B68]/30 focus:outline-none rounded-xl px-4 py-3 text-[15px] transition"
                placeholder="••••••••"
              />
            </label>

            <button
              type="submit"
              disabled={busy}
              data-testid="admin-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#2C4C3B] text-white hover:bg-[#1f372a] disabled:opacity-60 transition-colors rounded-full px-6 py-3.5 font-medium"
            >
              {busy ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

function formatDateTime(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
